import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { getAudienceOverlap } from '../../../../api_handlers/discovery';

const NAVY = '#111936';
const GOLD = '#FEBD1C';

const fmt = (n) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN');

function StatTile({ label, value, sub, accent }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 150,
        p: 2,
        borderRadius: 2,
        bgcolor: accent ? NAVY : '#f6f7fb',
        color: accent ? '#fff' : NAVY,
        border: `1px solid ${accent ? NAVY : '#e4e7ee'}`,
      }}
    >
      <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>
      {sub ? <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{sub}</Typography> : null}
    </Box>
  );
}

export default function AudienceUniqueReachModal({ open, setOpen, ids }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !ids || ids.length < 2) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    getAudienceOverlap(ids)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((e) => {
        if (alive) {
          const httpStatus = e?.response?.status;
          const body = e?.response?.data || {};
          const base =
            body.error ||
            (httpStatus === 401
              ? 'Your session could not be authorised for this request.'
              : 'Could not compute audience overlap for these creators.');
          setError(body.detail ? `${base} (${body.detail})` : base);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, ids]);

  const combined = data?.combined;

  return (
    <Dialog open={!!open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, fontWeight: 800, color: NAVY }}>
        Unique audience reach
        <IconButton
          onClick={() => setOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error">{error}</Alert>}

        {data && !error && (
          <>
            {/* combined summary */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <StatTile label="Gross reach (sum of followers)" value={fmt(combined?.gross_reach)} />
              <StatTile
                label="Estimated unique reach"
                value={fmt(combined?.estimated_unique_reach)}
                sub={`${combined?.redundancy_pct}% redundant`}
                accent
              />
              <StatTile
                label="Redundant (overlapping) audience"
                value={fmt(combined?.estimated_redundant_reach)}
              />
            </Box>

            {/* per-creator table */}
            <Typography sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>
              Per-creator contribution
            </Typography>
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Creator</TableCell>
                  <TableCell align="right">Followers</TableCell>
                  <TableCell align="right">
                    <Tooltip title="How distinct this creator's audience is from the others. Higher = brings more unique audience.">
                      <span>Differentiation</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Est. unique audience</TableCell>
                  <TableCell>Most distinct in</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.creators || []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 13 }}>
                        {c.name || c.handle || c.id}
                      </Typography>
                      {c.handle ? (
                        <Typography sx={{ fontSize: 11, color: '#667' }}>@{c.handle}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{fmt(c.followers)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <Box sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: '#eee', overflow: 'hidden' }}>
                          <Box
                            sx={{
                              width: `${Math.max(0, Math.min(100, c.differentiation_score))}%`,
                              height: '100%',
                              bgcolor: GOLD,
                            }}
                          />
                        </Box>
                        <span>{c.differentiation_score}</span>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {fmt(c.estimated_unique_followers)}
                      {c.estimated_unique_pct != null ? (
                        <Typography component="span" sx={{ fontSize: 11, color: '#667', ml: 0.5 }}>
                          ({c.estimated_unique_pct}%)
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {(c.top_differentiators || []).slice(0, 3).map((d) => (
                        <Chip key={d} label={d} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: 10 }} />
                      ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* pairwise similarity — ranked, with bars (clearer than a chip wall) */}
            {data.pairwise && data.pairwise.length ? (
              (() => {
                const nameOf = (id) => (data.creators.find((c) => c.id === id) || {}).name || id;
                const ranked = [...data.pairwise]
                  .filter((p) => p.similarity != null)
                  .sort((a, b) => (b.similarity || 0) - (a.similarity || 0));
                const top = ranked.slice(0, 8);
                const more = ranked.length - top.length;
                const barColor = (pct) => (pct >= 80 ? '#e0483d' : pct >= 60 ? '#f2a413' : '#3aa76d');
                return (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, color: NAVY, mb: 0.25 }}>
                      Most-alike pairs — highest audience overlap
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#98a2b3', mb: 1 }}>
                      Higher % = more overlapping (redundant) audience between the two creators.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                      {top.map((p) => {
                        const pct = Math.round(p.similarity * 100);
                        return (
                          <Box key={`${p.a_id}-${p.b_id}`}
                            sx={{ display: 'grid', gridTemplateColumns: '1fr 130px 42px', alignItems: 'center', gap: 1.5 }}>
                            <Typography noWrap sx={{ fontSize: 13, color: NAVY }}>
                              {nameOf(p.a_id)} <span style={{ color: '#98a2b3' }}>↔</span> {nameOf(p.b_id)}
                            </Typography>
                            <LinearProgress variant="determinate" value={pct}
                              sx={{ height: 8, borderRadius: 5, bgcolor: '#eef0f5',
                                    '& .MuiLinearProgress-bar': { backgroundColor: barColor(pct), borderRadius: 5 } }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, textAlign: 'right' }}>{pct}%</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {more > 0 && (
                      <Typography sx={{ fontSize: 12, color: '#98a2b3', mt: 1 }}>
                        + {more} more pair{more > 1 ? 's' : ''} with lower similarity
                      </Typography>
                    )}
                  </Box>
                );
              })()
            ) : null}

            {/* honesty caveat */}
            <Alert severity="info" sx={{ mt: 1, fontSize: 12 }}>
              {data.methodology}
            </Alert>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
